import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { Badge } from '../ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '../ui/tooltip';
import { Loader2, Search, Layers, Trash2 } from 'lucide-react';
import productionService from '../../services/productionService';
import clientService from '../../services/clientService';
import materialRequisitionService from '../../services/materialRequisitionService';
import inventoryService from '../../services/inventoryService';

function BatchCreateMaterialRequisitionDialog({ open, onOpenChange, onSuccess }) {
  // ========== Loading States ==========
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [loadingClients, setLoadingClients] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  // ========== Data ==========
  const [orders, setOrders] = useState([]);
  const [clients, setClients] = useState([]);

  // ========== Selection States ==========
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [orderSearch, setOrderSearch] = useState('');

  // ========== Aggregated Materials ==========
  const [aggregatedMaterials, setAggregatedMaterials] = useState([]);
  // Format: [{ orderId, orderNumber, product_id, product_code, product_name, quantity, unit, ... }]

  // ========== Inventory & Allocations ==========
  const [inventoryByProduct, setInventoryByProduct] = useState({});
  // { product_code: [{ location, available, source_client_id, source_client_name }] }
  const [materialAllocations, setMaterialAllocations] = useState({});
  // { product_code: [{ location, quantity, source_client_id, source_client_name }] }
  const [loadingInventory, setLoadingInventory] = useState(false);

  // ========== Step ==========
  const [step, setStep] = useState(1);

  // ========== Form ==========
  const [formData, setFormData] = useState({
    client_id: '',
    client_name: '',
    notes: ''
  });

  // ========== Reset on open ==========
  useEffect(() => {
    if (open) {
      setSelectedOrderIds(new Set());
      setOrderSearch('');
      setAggregatedMaterials([]);
      setInventoryByProduct({});
      setMaterialAllocations({});
      setLoadingInventory(false);
      setError(null);
      setStep(1);
      setFormData({
        client_id: '',
        client_name: '',
        notes: ''
      });
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      loadOrders();
      loadClients();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const loadOrders = useCallback(async () => {
    try {
      setLoadingOrders(true);
      // Load both released and in_progress orders
      const [releasedRes, inProgressRes] = await Promise.all([
        productionService.getProductionOrders({ status: 'released', page_size: 200 }),
        productionService.getProductionOrders({ status: 'in_progress', page_size: 200 })
      ]);

      const releasedItems = releasedRes.items || releasedRes.data?.items || [];
      const inProgressItems = inProgressRes.items || inProgressRes.data?.items || [];

      // Merge and deduplicate by _id
      const merged = [...releasedItems];
      const seenIds = new Set(merged.map(o => o._id));
      inProgressItems.forEach(o => {
        if (!seenIds.has(o._id)) {
          merged.push(o);
          seenIds.add(o._id);
        }
      });

      setOrders(merged);
    } catch (err) {
      console.error('Failed to load production orders:', err);
      setError('加载工单列表失败');
    } finally {
      setLoadingOrders(false);
    }
  }, []);

  const loadClients = useCallback(async () => {
    try {
      setLoadingClients(true);
      const result = await clientService.getClients(1, 200);
      const list = result.clients || [];
      setClients(list);
    } catch (err) {
      console.error('Failed to load clients:', err);
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }, []);

  // ========== Derived: Filtered Orders ==========
  const filteredOrders = useMemo(() => {
    if (!orderSearch.trim()) return orders;
    const term = orderSearch.toLowerCase();
    return orders.filter(o => {
      const poId = (o.production_order_id || '').toLowerCase();
      const code = (o.finished_product_code || '').toLowerCase();
      const name = (o.finished_product_name || '').toLowerCase();
      const clientName = (o.client_name || '').toLowerCase();
      return poId.includes(term) || code.includes(term) || name.includes(term) || clientName.includes(term);
    });
  }, [orders, orderSearch]);

  // ========== Handlers ==========
  const toggleOrder = (orderId) => {
    setSelectedOrderIds(prev => {
      const next = new Set(prev);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  };

  const handleNextStep = async () => {
    if (step === 1) {
      if (selectedOrderIds.size === 0) {
        setError('请至少选择一个工单');
        return;
      }
      setError(null);
      await loadMaterialsForOrders();
      setStep(2);
    } else if (step === 2) {
      if (aggregatedMaterials.length === 0) {
        setError('请至少保留一种物料');
        return;
      }
      setError(null);
      setStep(3);
    }
  };

  const handlePrevStep = () => {
    setStep(prev => Math.max(1, prev - 1));
    setError(null);
  };

  const loadMaterialsForOrders = async () => {
    setLoadingMaterials(true);
    const materialMap = new Map();

    try {
      const selectedOrders = orders.filter(o => selectedOrderIds.has(o._id));

      for (const order of selectedOrders) {
        try {
          // Get full order details to use pre-calculated material_requirements
          const orderDetail = await productionService.getProductionOrder(order._id);
          const items = orderDetail.items || [];

          // Collect and merge materials across ALL selected work orders by product_code
          items.forEach(item => {
            const materialRequirements = item.material_requirements || [];
            materialRequirements.forEach(mat => {
              const productCode = mat.material_product_code || mat.material_code || '';
              if (!productCode) return;

              if (materialMap.has(productCode)) {
                // Merge quantity for same material across all orders
                const existing = materialMap.get(productCode);
                existing.quantity += (mat.required_quantity ?? mat.quantity ?? 0);
                // Track source orders for display
                if (!existing.sourceOrders.some(o => o.orderId === order._id)) {
                  existing.sourceOrders.push({
                    orderId: order._id,
                    orderNumber: order.production_order_id,
                    finishedProductCode: order.finished_product_code
                  });
                }
              } else {
                materialMap.set(productCode, {
                  _key: `${productCode}-${Math.random().toString(36).slice(2, 7)}`,
                  product_id: mat.material_product_id || mat._id || '',
                  product_code: productCode,
                  product_name: mat.material_name || mat.material_product_name || '',
                  quantity: mat.required_quantity ?? mat.quantity ?? 1,
                  unit: mat.unit || '个',
                  specification: mat.specification || '',
                  sourceOrders: [{
                    orderId: order._id,
                    orderNumber: order.production_order_id,
                    finishedProductCode: order.finished_product_code
                  }]
                });
              }
            });
          });
        } catch (err) {
          console.warn(`Failed to load order details for ${order.production_order_id}:`, err);
        }
      }

      // Convert map to array
      const allMaterials = Array.from(materialMap.values());

      // Sort by tip diameter ascending (extracted from product code like RM-U20-8-310)
      allMaterials.sort((a, b) => {
        const getTipDiameter = (code) => {
          if (!code) return 0;
          const match = code.match(/RM-U20-([\d.]+)-\d+/);
          return match ? parseFloat(match[1]) : 0;
        };
        return getTipDiameter(a.product_code) - getTipDiameter(b.product_code);
      });

      setAggregatedMaterials(allMaterials);
    } catch (err) {
      console.error('Failed to load materials:', err);
      setError('加载物料列表失败');
    } finally {
      setLoadingMaterials(false);
    }
  };

  const updateMaterialQuantity = (key, quantity) => {
    setAggregatedMaterials(prev => prev.map(m =>
      m._key === key ? { ...m, quantity: parseFloat(quantity) || 0 } : m
    ));
  };

  // Update allocation quantity for a specific material + location
  const updateAllocation = (productCode, location, quantity) => {
    setMaterialAllocations(prev => {
      const allocs = prev[productCode] ? [...prev[productCode]] : [];
      const idx = allocs.findIndex(a => a.location === location);
      const qty = parseFloat(quantity) || 0;
      if (qty > 0) {
        if (idx >= 0) {
          allocs[idx] = { ...allocs[idx], quantity: qty };
        } else {
          const inv = (inventoryByProduct[productCode] || []).find(i => i.location === location);
          allocs.push({
            location,
            quantity: qty,
            source_client_id: inv?.source_client_id || null,
            source_client_name: inv?.source_client_name || ''
          });
        }
      } else {
        if (idx >= 0) allocs.splice(idx, 1);
      }
      return { ...prev, [productCode]: allocs };
    });
  };

  // Get total allocated quantity for a material
  const getAllocatedTotal = (productCode) => {
    return (materialAllocations[productCode] || []).reduce((sum, a) => sum + (a.quantity || 0), 0);
  };

  const removeMaterial = (key) => {
    setAggregatedMaterials(prev => prev.filter(m => m._key !== key));
  };

  const handleClientChange = (value) => {
    const client = clients.find(c => c._id === value);
    setFormData(prev => ({
      ...prev,
      client_id: value,
      client_name: client?.name || client?.client_name || ''
    }));
    // Clear previous inventory when client changes
    setInventoryByProduct({});
    setMaterialAllocations({});
  };

  // Load inventory for selected materials when in step 3 with a client selected
  const loadInventoryForMaterials = useCallback(async (clientId) => {
    if (!clientId || aggregatedMaterials.length === 0) return;
    setLoadingInventory(true);
    try {
      // Fetch inventory for this client, excluding zero stock
      const response = await inventoryService.getInventory(
        1, 1000, 'name', 'asc',
        {}, clientId, true
      );
      const items = response.inventory || [];
      // Group by product_code, filtering to ONLY the selected client's stock
      const byProduct = {};
      items.forEach(item => {
        const code = item.product_code || item.product_id;
        if (!code) return;
        // Filter: only keep stock belonging to the selected client
        // API may return global view, so we filter by matching client_id or source_client_id
        const itemClientId = item.source_client_id || item.client_id;
        if (itemClientId && itemClientId !== clientId) {
          return; // Skip stock from other clients
        }
        if (!byProduct[code]) byProduct[code] = [];
        const available = item.remaining_quantity ?? item.available_quantity ?? 0;
        if (available > 0) {
          byProduct[code].push({
            location: item.location || 'main',
            available,
            source_client_id: item.source_client_id || item.client_id || null,
            source_client_name: item.source_client_name || item.client_name || ''
          });
        }
      });
      setInventoryByProduct(byProduct);
    } catch (err) {
      console.error('Failed to load inventory:', err);
      setError('加载库存分布失败，请重试');
    } finally {
      setLoadingInventory(false);
    }
  }, [aggregatedMaterials]);

  // Auto-trigger inventory load when entering step 3 with client selected
  useEffect(() => {
    if (step === 3 && formData.client_id && Object.keys(inventoryByProduct).length === 0) {
      loadInventoryForMaterials(formData.client_id);
    }
  }, [step, formData.client_id, inventoryByProduct, loadInventoryForMaterials]);

  // Auto-allocate quantities across locations when inventory loads
  useEffect(() => {
    if (Object.keys(inventoryByProduct).length === 0) return;
    const newAllocations = {};
    aggregatedMaterials.forEach(mat => {
      const invList = inventoryByProduct[mat.product_code] || [];
      let remaining = mat.quantity;
      const allocs = [];
      for (const inv of invList) {
        if (remaining <= 0) break;
        const qty = Math.min(remaining, inv.available);
        if (qty > 0) {
          allocs.push({
            location: inv.location,
            quantity: qty,
            source_client_id: inv.source_client_id,
            source_client_name: inv.source_client_name
          });
          remaining -= qty;
        }
      }
      if (allocs.length > 0) {
        newAllocations[mat.product_code] = allocs;
      }
    });
    setMaterialAllocations(newAllocations);
  }, [inventoryByProduct, aggregatedMaterials]);

  const handleSubmit = async () => {
    if (!formData.client_id) {
      setError('请选择出库来源客户');
      return;
    }

    // Validate allocations: check if any material is not fully allocated
    const underAllocated = aggregatedMaterials.filter(m => {
      const allocated = getAllocatedTotal(m.product_code);
      return allocated < m.quantity;
    });
    const overAllocated = aggregatedMaterials.filter(m => {
      const allocated = getAllocatedTotal(m.product_code);
      return allocated > m.quantity;
    });
    if (underAllocated.length > 0) {
      setError(`以下物料分配不足: ${underAllocated.map(m => m.product_code).join(', ')}`);
      return;
    }
    if (overAllocated.length > 0) {
      setError(`以下物料超量分配: ${overAllocated.map(m => m.product_code).join(', ')}`);
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // Collect all unique work orders from selected orders
      const selectedOrders = orders.filter(o => selectedOrderIds.has(o._id));
      const workOrders = selectedOrders.map(o => ({
        production_order_id: o._id,
        production_order_number: o.production_order_id,
        finished_product_code: o.finished_product_code || '',
        finished_product_name: o.finished_product_name || '',
        planned_quantity: o.planned_quantity || o.total_planned_quantity || 0
      }));

      // Build products array from merged materials
      // Each product may come from multiple orders; annotate with the first source order
      const products = aggregatedMaterials.map(m => {
        const allocs = materialAllocations[m.product_code] || [];
        const product = {
          product_id: m.product_id || m.product_code,
          quantity_requested: m.quantity,
          unit: m.unit,
          notes: m.sourceOrders && m.sourceOrders.length > 1
            ? `合并自 ${m.sourceOrders.length} 个工单`
            : `用于工单 ${m.sourceOrders?.[0]?.orderNumber || ''}`,
          source_production_orders: m.sourceOrders || []
        };
        // Include location allocations if user has assigned specific locations
        if (allocs.length > 0) {
          product.location_allocations = allocs.map(a => ({
            location: a.location,
            quantity: a.quantity,
            source_client_id: a.source_client_id,
            source_client_name: a.source_client_name
          }));
          // Set primary location to the first allocation for backward compatibility
          product.location = allocs[0].location;
        }
        return product;
      });

      const data = {
        work_orders: workOrders,
        products: products,
        client_id: formData.client_id,
        client_name: formData.client_name,
        notes: formData.notes || `多工单合并领料，共 ${workOrders.length} 个工单`
      };

      const result = await materialRequisitionService.createRequisition(data);

      if (onSuccess) onSuccess(result);
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to create batch material requisition:', err);
      setError(err.response?.data?.error || err.message || '创建批量领料申请失败');
    } finally {
      setSubmitting(false);
    }
  };

  // ========== Stats ==========
  const totalMaterialQuantity = useMemo(() => {
    return aggregatedMaterials.reduce((sum, m) => sum + (m.quantity || 0), 0);
  }, [aggregatedMaterials]);

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!submitting) onOpenChange(v); }}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Layers className="h-5 w-5 text-blue-600" />
            批量合并领料
          </DialogTitle>
          <DialogDescription>
            选择多个工单，聚合BOM物料后统一创建领料申请
          </DialogDescription>
        </DialogHeader>

        {/* Step Indicator */}
        <div className="flex items-center gap-2 mb-2">
          {[1, 2, 3].map(s => (
            <React.Fragment key={s}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                s === step ? 'bg-blue-600 text-white' :
                s < step ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-500'
              }`}>
                {s < step ? '✓' : s}
              </div>
              {s < 3 && <div className={`w-8 h-0.5 ${s < step ? 'bg-green-500' : 'bg-gray-200'}`} />}
            </React.Fragment>
          ))}
          <span className="ml-2 text-sm text-muted-foreground">
            {step === 1 && '选择工单'}
            {step === 2 && '确认物料'}
            {step === 3 && '填写信息'}
          </span>
        </div>

        <div className="space-y-4 py-2">
          {error && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {error}
            </div>
          )}

          {/* ========== Step 1: Select Orders ========== */}
          {step === 1 && (
            <div className="space-y-2">
              <Label className="text-base font-medium">
                选择工单 ({selectedOrderIds.size} 个已选)
              </Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="搜索工单号、产品代码、名称或客户..."
                  value={orderSearch}
                  onChange={(e) => setOrderSearch(e.target.value)}
                  className="pl-9"
                  disabled={loadingOrders}
                />
              </div>

              <div className="rounded-md border max-h-64 overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">选择</TableHead>
                      <TableHead>工单号</TableHead>
                      <TableHead>产品</TableHead>
                      <TableHead>客户</TableHead>
                      <TableHead className="text-center">计划数量</TableHead>
                      <TableHead className="text-center">状态</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingOrders ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-4">
                          <Loader2 className="h-4 w-4 animate-spin mx-auto" />
                        </TableCell>
                      </TableRow>
                    ) : filteredOrders.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-4">
                          暂无符合条件的工单
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredOrders.map(order => (
                        <TableRow
                          key={order._id}
                          className={selectedOrderIds.has(order._id) ? 'bg-blue-50' : ''}
                          onClick={() => toggleOrder(order._id)}
                          style={{ cursor: 'pointer' }}
                        >
                          <TableCell onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selectedOrderIds.has(order._id)}
                              onChange={() => toggleOrder(order._id)}
                              className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                            />
                          </TableCell>
                          <TableCell className="font-medium">{order.production_order_id}</TableCell>
                          <TableCell>
                            <div className="font-medium">{order.finished_product_code}</div>
                            <div className="text-xs text-muted-foreground">{order.finished_product_name}</div>
                          </TableCell>
                          <TableCell>{order.client_name || '-'}</TableCell>
                          <TableCell className="text-center">
                            {order.planned_quantity || order.total_planned_quantity || '-'}
                          </TableCell>
                          <TableCell className="text-center">
                            <Badge variant={order.status === 'in_progress' ? 'warning' : 'default'}>
                              {order.status === 'in_progress' ? '生产中' : '已发布'}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}

          {/* ========== Step 2: Confirm Materials ========== */}
          {step === 2 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-base font-medium">
                  确认物料 ({aggregatedMaterials.length} 项，共 {totalMaterialQuantity} 件)
                </Label>
              </div>

              {loadingMaterials ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : aggregatedMaterials.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  所选工单暂无BOM物料
                </div>
              ) : (
                <div className="rounded-md border max-h-80 overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>来源工单</TableHead>
                        <TableHead>物料编码</TableHead>
                        <TableHead>物料名称</TableHead>
                        <TableHead className="text-center">数量</TableHead>
                        <TableHead>单位</TableHead>
                        <TableHead className="w-10"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {aggregatedMaterials.map((mat) => (
                        <TableRow key={mat._key}>
                          <TableCell>
                            {mat.sourceOrders?.length > 1 ? (
                              <TooltipProvider delayDuration={100}>
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div className="cursor-help">
                                      <div className="font-medium text-sm">{mat.sourceOrders[0].orderNumber} 等</div>
                                      <div className="text-xs text-muted-foreground">
                                        {mat.sourceOrders.length} 个工单
                                      </div>
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="right" className="max-w-xs">
                                    <div className="space-y-1">
                                      <div className="font-semibold text-xs text-muted-foreground">来源工单</div>
                                      {mat.sourceOrders.map((so, idx) => (
                                        <div key={idx} className="text-sm">
                                          {so.orderNumber} <span className="text-muted-foreground">({so.finishedProductCode})</span>
                                        </div>
                                      ))}
                                    </div>
                                  </TooltipContent>
                                </Tooltip>
                              </TooltipProvider>
                            ) : (
                              <div>
                                <div className="font-medium text-sm">{mat.sourceOrders?.[0]?.orderNumber}</div>
                                <div className="text-xs text-muted-foreground">{mat.sourceOrders?.[0]?.finishedProductCode}</div>
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-sm">{mat.product_code}</TableCell>
                          <TableCell>{mat.product_name}</TableCell>
                          <TableCell className="text-center">
                            <Input
                              type="number"
                              min="0"
                              step="0.01"
                              value={mat.quantity}
                              onChange={(e) => updateMaterialQuantity(mat._key, e.target.value)}
                              className="h-8 w-20 mx-auto text-center"
                            />
                          </TableCell>
                          <TableCell>{mat.unit}</TableCell>
                          <TableCell>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeMaterial(mat._key)}
                              className="text-destructive hover:text-destructive h-8 w-8 p-0"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          )}

          {/* ========== Step 3: Fill Info ========== */}
          {step === 3 && (
            <div className="space-y-4">
              <Label className="text-base font-medium">填写领料信息</Label>

              <div className="grid grid-cols-1 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="batch-client">
                    出库来源客户 <span className="text-red-500">*</span>
                  </Label>
                  <Select
                    value={formData.client_id}
                    onValueChange={handleClientChange}
                    disabled={loadingClients || submitting}
                  >
                    <SelectTrigger id="batch-client">
                      <SelectValue placeholder={loadingClients ? '加载中...' : '选择客户'} />
                    </SelectTrigger>
                    <SelectContent>
                      {clients.map(client => (
                        <SelectItem key={client._id} value={client._id}>
                          {client.name || client.client_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground">
                    选择从哪个客户的库存中扣减物料
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="batch-notes">备注</Label>
                  <Input
                    id="batch-notes"
                    value={formData.notes}
                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                    placeholder="领料备注..."
                    disabled={submitting}
                  />
                </div>
              </div>

              {/* Material Location Allocation */}
              {formData.client_id && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">物料库位分配</Label>
                    {loadingInventory && (
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        加载库存...
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    为每种物料指定从哪些库位出库，已按可用库存自动分配，可手动调整
                  </p>

                  {loadingInventory ? (
                    <div className="flex justify-center py-8">
                      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                    </div>
                  ) : (
                    <div className="rounded-md border max-h-80 overflow-y-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>物料编码</TableHead>
                            <TableHead>物料名称</TableHead>
                            <TableHead className="text-center">所需</TableHead>
                            <TableHead className="text-center">已分配</TableHead>
                            <TableHead>库位分配</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {aggregatedMaterials.map((mat) => {
                            const invList = inventoryByProduct[mat.product_code] || [];
                            const allocated = getAllocatedTotal(mat.product_code);
                            const isFullyAllocated = allocated >= mat.quantity;
                            const isOverAllocated = allocated > mat.quantity;
                            return (
                              <TableRow key={mat._key}>
                                <TableCell className="font-mono text-xs">{mat.product_code}</TableCell>
                                <TableCell className="text-sm">{mat.product_name}</TableCell>
                                <TableCell className="text-center text-sm">{mat.quantity}</TableCell>
                                <TableCell className="text-center">
                                  <span className={`text-sm font-medium ${isOverAllocated ? 'text-red-600' : isFullyAllocated ? 'text-green-600' : 'text-amber-600'}`}>
                                    {allocated}
                                  </span>
                                </TableCell>
                                <TableCell>
                                  {invList.length === 0 ? (
                                    <span className="text-xs text-red-500">无库存</span>
                                  ) : (
                                    <div className="space-y-1">
                                      {invList.map((inv) => {
                                        const alloc = (materialAllocations[mat.product_code] || []).find(a => a.location === inv.location);
                                        const val = alloc?.quantity ?? 0;
                                        return (
                                          <div key={inv.location} className="flex items-center gap-2">
                                            <span className="text-xs text-muted-foreground w-16">{inv.location}:</span>
                                            <Input
                                              type="number"
                                              min="0"
                                              max={inv.available}
                                              step="0.01"
                                              value={val || ''}
                                              onChange={(e) => updateAllocation(mat.product_code, inv.location, e.target.value)}
                                              className="h-6 w-16 text-xs px-1 text-center"
                                            />
                                            <span className="text-xs text-muted-foreground">(可用 {inv.available})</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}

                  {/* Allocation summary */}
                  {!loadingInventory && (
                    <div className="text-xs flex gap-4 text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-green-500 inline-block" />
                        已足额分配
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-amber-500 inline-block" />
                        分配不足
                      </span>
                      <span className="flex items-center gap-1">
                        <span className="w-2 h-2 rounded-full bg-red-500 inline-block" />
                        超量分配
                      </span>
                    </div>
                  )}
                </div>
              )}

              {/* Summary */}
              <div className="text-sm bg-gray-50 p-3 rounded border">
                <div className="font-medium mb-1">汇总</div>
                <div className="grid grid-cols-2 gap-2 text-muted-foreground">
                  <div>已选工单: {selectedOrderIds.size} 个</div>
                  <div>物料项数: {aggregatedMaterials.length} 项</div>
                  <div>总数量: {totalMaterialQuantity} 件</div>
                  <div>出库客户: {formData.client_name || '未选择'}</div>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => step === 1 ? onOpenChange(false) : handlePrevStep()}
            disabled={submitting}
          >
            {step === 1 ? '取消' : '上一步'}
          </Button>

          {step < 3 ? (
            <Button onClick={handleNextStep} disabled={loadingMaterials}>
              {loadingMaterials ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  加载中...
                </>
              ) : (
                '下一步'
              )}
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              disabled={submitting || !formData.client_id || loadingInventory}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {submitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  创建中...
                </>
              ) : (
                <>创建领料申请 ({aggregatedMaterials.length})</>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default BatchCreateMaterialRequisitionDialog;
